import type { CanMatchFn, Routes } from '@angular/router';
import { adminGuard } from './core/auth/admin.guard';
import { authGuard } from './core/auth/auth.guard';
import { orderPaymentGuard } from './core/auth/order-payment.guard';
import { isLegalSlug } from './core/legal/documents';

/** Un seul écran pour CGU, CGV, mentions, etc. — l'URL reste un slug lisible. */
const matchLegalDocument: CanMatchFn = (_route, segments) =>
  segments.length === 1 && isLegalSlug(segments[0].path);

export const routes: Routes = [
  // `wide` élargit la colonne de la coquille : la découverte liste des cartes
  // en grille, là où les formulaires restent sur une colonne étroite.
  {
    path: '',
    pathMatch: 'full',
    data: { wide: true },
    loadComponent: () => import('./pages/home/home').then((m) => m.Home),
  },
  // Espace producteur. Déclaré avant `producteur/:id`, qui capturerait sinon
  // « tableau-de-bord » comme identifiant de ferme et afficherait une fiche
  // introuvable. Le garde d'authentification suffit : aucun rôle « producteur »
  // n'est exigé, puisque c'est ce parcours qui permet de l'obtenir.
  {
    path: 'producteur/tableau-de-bord',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-dashboard/producer-dashboard').then((m) => m.ProducerDashboard),
  },
  {
    path: 'producteur/paliers',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-tiers/producer-tiers').then((m) => m.ProducerTiersPage),
  },
  {
    path: 'producteur/boutique',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-shop/producer-shop').then((m) => m.ProducerShopPage),
  },
  {
    path: 'producteur/affiche',
    canActivate: [authGuard],
    data: { wide: true },
    loadComponent: () =>
      import('./pages/producer-poster/producer-poster').then((m) => m.ProducerPoster),
  },
  {
    path: 'producteur/points-de-retrait',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-pickup-points/producer-pickup-points').then(
        (m) => m.ProducerPickupPoints,
      ),
  },
  {
    path: 'producteur/equipe',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/producer-team/producer-team').then((m) => m.ProducerTeam),
  },
  {
    path: 'producteur/produits',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-products/producer-products').then((m) => m.ProducerProducts),
  },
  {
    path: 'producteur/commandes',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-orders/producer-orders').then((m) => m.ProducerOrdersPage),
  },
  {
    path: 'producteur/preparation/scan',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-prep-scan/producer-prep-scan').then((m) => m.ProducerPrepScanPage),
  },
  {
    path: 'producteur/scan',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-scan/producer-scan').then((m) => m.ProducerScanPage),
  },
  {
    path: 'producteur/avis',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-reviews/producer-reviews').then((m) => m.ProducerReviewsPage),
  },
  {
    path: 'producteur/succes',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-achievements/producer-achievements').then(
        (m) => m.ProducerAchievementsPage,
      ),
  },
  {
    path: 'producteur/statistiques',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-stats/producer-stats').then((m) => m.ProducerStatsPage),
  },
  {
    path: 'scan',
    loadComponent: () => import('./pages/client-scan/client-scan').then((m) => m.ClientScanPage),
  },
  {
    path: 'panier',
    loadComponent: () => import('./pages/cart/cart').then((m) => m.CartPage),
  },
  {
    path: 'panier/restaurer/:snapshotId',
    loadComponent: () =>
      import('./pages/cart-restore/cart-restore').then((m) => m.CartRestorePage),
  },
  {
    path: 'commandes/paiement',
    canActivate: [orderPaymentGuard],
    loadComponent: () =>
      import('./pages/order-payment/order-payment').then((m) => m.OrderPaymentPage),
  },
  {
    path: 'commande/:orderId',
    loadComponent: () => import('./pages/guest-order/guest-order').then((m) => m.GuestOrderPage),
  },
  {
    path: 'mes-commandes',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/my-orders/my-orders').then((m) => m.MyOrdersPage),
  },
  {
    path: 'mes-retraits',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/pickup-calendar/pickup-calendar').then((m) => m.PickupCalendarPage),
  },
  {
    path: 'producteur/paiement',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-dashboard/payment-onboarding-resume').then(
        (m) => m.PaymentOnboardingResume,
      ),
  },
  // Tient lieu du formulaire hébergé par le prestataire de paiement, tant que
  // le compte prestataire n'est pas ouvert (prestataire stub).
  {
    path: 'producteur/paiement/simulation',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/producer-dashboard/payment-onboarding-stub').then(
        (m) => m.PaymentOnboardingStub,
      ),
  },
  // Fiche produit publique — déclarée avant `producteur/:id` pour éviter qu'un
  // segment soit pris pour l'identifiant de la ferme.
  {
    path: 'producteur/:producerId/produit/:productId',
    loadComponent: () => import('./pages/product/product').then((m) => m.ProductPage),
  },
  // Fiche publique d'une ferme : consultable sans compte, comme le catalogue.
  {
    path: 'producteur/:id',
    data: { wide: true },
    loadComponent: () => import('./pages/producer/producer').then((m) => m.ProducerPage),
  },
  // Entrée directe : le client arrive chez le producteur, pas sur le marché.
  {
    path: 'chez/:slug',
    data: { wide: true, shopChrome: true },
    loadComponent: () => import('./pages/producer/producer').then((m) => m.ProducerPage),
  },
  {
    path: 'connexion',
    pathMatch: 'full',
    loadComponent: () => import('./pages/sign-in/sign-in').then((m) => m.SignIn),
  },
  // Atterrissage du lien reçu par e-mail. Le jeton est dans le fragment d'URL,
  // que le routeur ignore : c'est l'écran qui le lit.
  {
    path: 'connexion/lien',
    loadComponent: () => import('./pages/magic-link/magic-link').then((m) => m.MagicLink),
  },
  {
    path: 'creer-un-compte',
    loadComponent: () => import('./pages/sign-up/sign-up').then((m) => m.SignUp),
  },
  {
    path: 'mot-de-passe-oublie',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password').then((m) => m.ForgotPassword),
  },
  {
    path: 'design',
    loadComponent: () => import('./pages/design-system/design-system').then((m) => m.DesignSystem),
  },
  // Le profil exige un compte, contrairement au catalogue : le garde n'est posé
  // que sur les routes qui manipulent des données personnelles ou une commande.
  {
    path: 'admin',
    canActivate: [adminGuard],
    data: { wide: true },
    loadComponent: () =>
      import('./pages/admin-dashboard/admin-dashboard').then((m) => m.AdminDashboardPage),
  },
  {
    path: 'profil',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/profile/profile').then((m) => m.Profile),
  },
  {
    path: 'preferences',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/preferences/preferences').then((m) => m.Preferences),
  },
  // Page d'accueil producteur : publique, distincte du formulaire d'activation.
  {
    path: 'vendre',
    pathMatch: 'full',
    data: { wide: true },
    loadComponent: () => import('./pages/sell/sell').then((m) => m.SellPage),
  },
  {
    path: 'vendre/activer',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/become-producer/become-producer').then((m) => m.BecomeProducer),
  },
  {
    path: ':slug',
    canMatch: [matchLegalDocument],
    loadComponent: () => import('./pages/legal/legal').then((m) => m.LegalPage),
  },
  { path: '**', redirectTo: '' },
];
